import { centralSupportChoice, centralSupportChoices, centralSupportOptionsForRM5RM10Evo } from "projectDesign/components/projectConfiguration/fields/centralSupportChoice";
import { SOIL_CLASS } from "projectDesign/components/projectConfiguration/fields/soilClass";
import { RoofExposureOption } from "projectDesign/components/projectConfiguration/fields/types/roofExposure";
import { calculateKe, calculateKz, getFrictionFactor, get_design_life_factor, get_numerical_coefficient } from "projectDesign/components/projectConfiguration/utils/coefficients";
import { isASCE710, isASCE716or722, isASCE722 } from "__common/constants/buildingCodes";
import { isRM10, isRMIFIProduct, isRM10Evolution, isRMGridFlex, isRMDT, isRM5, isRmGridflex10D, isEcoFoot2Plus, isRmGridflex5Rwdi } from "__common/constants/products";
import store from "__common/store";
import { greaterThanEqualToProjectVersion, inRangeProjectVersion } from "__common/utils/versionCompare/versionCompare";
import { VERSION_MAP } from "__common/utils/versionCompare/version_info";
import { MAX_BLOCK_COUNTS, interiorBayCapacity, isHalfBlockAllowed, minBlockCount, rmGridflex10MaxBlockCount, rm5MaxBlockCount, rmGridflex10MaxBlockNorthCornerCount, rmGridflex5dMaxBlockCount } from "projectDesign/components/projectConfiguration/utils/constants";
import { AttachmentOptimization } from "projectDesign/components/projectConfiguration/fields/attachmentOptimizationCriteria";
import { flat } from "__common/constants/buildingTypes";
export function getRMProjectConfigurationData(projectEnvConfig: projectEnvConfig) {
  const {
    product,
    product_id,
    zipcode,
    building_code,
    wind_speed,
    snow_load,
    seismic_ss,
    seismic_s1,
    elevation,
    risk_category,
    dead_load_factor_modification,
    mean_recurrence_interval,
    building_height,
    roof_type,
    sub_category,
    manufacturer,
    colour,
    choice_of_fastend,
    parapet_height,
    parapet_height_input,
    setback_distance,
    is_half_block_allowed,
    block_weight,
    half_block_weight,
    wind_exposure,
    building_length,
    env_factors_for,
    client_address,
    client_name,
    created,
    updated,
    created_user_time,
    imported_helioscope_layout,
    deflectors,
    allow_mechanical_attachments,
    limit_down_point_loads,
    max_down_point_load_to_be_allowed,
    allow_manual_attachments,
    attachment_optimization_criteria,
    roof_psf_limit,
    total_weight_on_roof_limit,
    attachment_type,
    soil_class,
    psf_control,
    uplift_allowable,
    shear_allowable,
    topographical_factor_kzt,
    velocity_pressure_exposure_coefficient_kz,
    numberical_coefficient,
    design_life_factor_fc,
    wind_directionality_factor_kd,
    roof_exposure,
    ground_elevation_factor_ke,
    shortest_building_length,
    tilt,
    central_rails_count,
    allow_north_bays,
    central_support,
    add_roof_pads_everywhere,
    use_friction,
    allow_secondary_ballast_tray,
    allow_dual_attachments,
    anchor_type,
    created_user_is_staff,
    module_type,
    building_height_greater_than_6_storey,
    nbcc_category,
    nbcc_roughness,
    nbcc_wind_pressure,
    rain_load,
    mandatory_mid_support,
    additional_rail, 
    blocks_orientation,
    seismic_sds,
    seismic_sd1,
    tornado_speed,
    helioscope_id,
    thermal_factor,
    use_default_flat_slope_roof_snow_load,
    flat_roof_snow_load,
    friction_factor,
    load_sharing_type,
    fully_adhered,
    seismic_ip,
    snow_is,
    wind_iw,
    is_ul3741_required,
    central_support_choice,
    max_block_count,
    min_block_count,
    interior_bay_capacity,
    use_default_block_capacities,
    use_default_max_block_north_corner,
    max_block_north_corner_count,
    domestic_content,
    building_type,
  } = projectEnvConfig;
  const {
    projectConfiguration,moduleSelector: { modelData },
  } = store.getState();
  return {
    // RM roofs are always flat. Projects saved before building_type was carried
    // through this whitelist have no value, so fall back rather than leaving it
    // undefined - isFlatRoof(undefined) is false and silently disables the flat
    // roof branches that read this field.
    building_type: building_type ?? flat.value,
    zipcode,
    building_code,
    wind_speed,
    snow_load,
    seismic_ss,
    elevation,
    risk_category,
    mean_recurrence_interval,
    dead_load_factor_modification,
    building_height,
    roof_type,
    parapet_height,
    parapet_height_input,
    setback_distance,
    block_weight,
    wind_exposure,
    building_length,
    env_factors_for,
    client_address,
    client_name,
    created,
    updated,
    created_user_time,
    imported_helioscope_layout,
    helioscope_id,
    deflectors,
    allow_mechanical_attachments,
    ...(isRMIFIProduct(product) ?
      {
        product_id,
        tilt: tilt || 5,
        soil_class: soil_class || SOIL_CLASS.D_DEFAULT,
        topographical_factor_kzt,
        central_rails_count,
        allow_manual_attachments,
        allow_secondary_ballast_tray,
        allow_dual_attachments,
      } : {}),
    ...((isRMIFIProduct(product) || isRM10(product) || isRM10Evolution(product) || isRM5(product)) || isRmGridflex10D(product) || isRmGridflex5Rwdi(product) ?
      {
        seismic_s1,
        attachment_optimization_criteria,
        roof_psf_limit,
        total_weight_on_roof_limit,
        attachment_type,
        psf_control,
        uplift_allowable,
        shear_allowable,
        topographical_factor_kzt,
        is_half_block_allowed,
        half_block_weight,
        anchor_type,
        building_height_greater_than_6_storey
      } : {}),
    ...(isRM10(product) || isRM10Evolution(product) || isRM5(product) || isRmGridflex10D(product) ? {
        nbcc_category,
        nbcc_roughness,
        nbcc_wind_pressure,
        rain_load,
      } : {}),
    ...(isRM10(product) || isRM10Evolution(product) || isRmGridflex10D(product) || isRmGridflex5Rwdi(product) || isRMGridFlex(product) || isRM5(product) ? {
      ...isRM10(product) ? {    
        nbcc_category,
        nbcc_roughness,
        nbcc_wind_pressure
      }: {},
      central_support,
      add_roof_pads_everywhere,
      use_friction,
      shortest_building_length,
      topographical_factor_kzt: topographical_factor_kzt || 1,
      velocity_pressure_exposure_coefficient_kz : velocity_pressure_exposure_coefficient_kz || calculateKz(wind_exposure, building_height, zipcode, projectConfiguration?.projectVersion, projectConfiguration?.inputUnit, nbcc_roughness, false, building_code),
      wind_directionality_factor_kd: wind_directionality_factor_kd || 0.85,
      roof_exposure: roof_exposure || RoofExposureOption.partiallyExposed.value,
      ground_elevation_factor_ke: ground_elevation_factor_ke || calculateKe(elevation, isASCE716or722(building_code), projectConfiguration?.inputUnit, projectConfiguration?.projectVersion, zipcode),
      numberical_coefficient: numberical_coefficient || get_numerical_coefficient(building_code, elevation, projectConfiguration?.inputUnit, zipcode, projectConfiguration?.projectVersion),
      design_life_factor_fc: mean_recurrence_interval === 50 && design_life_factor_fc === 0.8649000000000001 ? 1 : design_life_factor_fc ? design_life_factor_fc : get_design_life_factor(mean_recurrence_interval),
      soil_class: soil_class || ((inRangeProjectVersion(projectConfiguration?.projectVersion, VERSION_MAP['shortest_building_length_changes'],
      VERSION_MAP['soil_class_D_to_D_Default_changes']) || isASCE710((building_code))) ? SOIL_CLASS.D : SOIL_CLASS.D_DEFAULT),
      created_user_is_staff,
      allow_manual_attachments,
      } : {}),
    ...(isRM10Evolution(product) ? {
      max_block_count : (max_block_count == 0) ? 0 : max_block_count || MAX_BLOCK_COUNTS[product],
      min_block_count : (min_block_count == 0) ? 0 : min_block_count || minBlockCount(attachment_optimization_criteria === AttachmentOptimization.FULLY_ATTACHED, isHalfBlockAllowed(is_half_block_allowed), product_id),
      interior_bay_capacity : (interior_bay_capacity == 0) ? 0 : interior_bay_capacity || interiorBayCapacity(product, isHalfBlockAllowed(is_half_block_allowed)),
      allow_north_bays,
      domestic_content,
      } : {}),
    ...(isRM5(product) ? {
      use_default_block_capacities : use_default_block_capacities,
      max_block_count : (max_block_count == 0) ? 0 : max_block_count || rm5MaxBlockCount(isHalfBlockAllowed(is_half_block_allowed)),
      min_block_count : (min_block_count == 0) ? 0 : min_block_count || minBlockCount(attachment_optimization_criteria === AttachmentOptimization.FULLY_ATTACHED, isHalfBlockAllowed(is_half_block_allowed), product_id),
      central_support, module_type,
      allow_manual_attachments,
      domestic_content,
      } : {}),
    ...(isRMDT(product) ? {
      domestic_content,
      } : {}),
    ...(isRMGridFlex(product) || isRM10(product) || isRM10Evolution(product) || isRMDT(product) || isRM5(product) || isRmGridflex10D(product) || isRmGridflex5Rwdi(product) ? {
      limit_down_point_loads,
      max_down_point_load_to_be_allowed,
      } : {}),
    ...(isRM5(product) || isRMDT(product) ? {
      shortest_building_length,
      } : {}),
    ...(isRM5(product) ? {
      module_type,
      } : {}),
    ...(isRM10Evolution(product) || isRM10(product) || isRM5(product) ? {
        use_friction
      } : {}),
    ...(isRM10Evolution(product) || isRM10(product) ? {
        add_roof_pads_everywhere
      } : {}),
    ...(isRM10Evolution(product) || isRM5(product) || isRMGridFlex(product) || isRmGridflex10D(product) || isRmGridflex5Rwdi(product) ? {
      sub_category,
      manufacturer,
      colour,
      choice_of_fastend,
       } : {}),
      ...(isRMDT(product) ? {
        building_height_greater_than_6_storey,
      } : {}),
    ...(isRMGridFlex(product) || isRmGridflex10D(product) || isRmGridflex5Rwdi(product) ? {
      mandatory_mid_support,
      thermal_factor: thermal_factor || (isASCE722(building_code) ? 1.2 : 1),
      friction_factor : friction_factor || getFrictionFactor(roof_type, product),
      load_sharing_type: load_sharing_type || 'default',
      seismic_ip: seismic_ip,
      snow_is: snow_is,
      fully_adhered: fully_adhered,
      wind_iw: wind_iw
      } : {}),
    ...(isRmGridflex10D(product) || isRmGridflex5Rwdi(product) ? {
      use_default_flat_slope_roof_snow_load: use_default_flat_slope_roof_snow_load == null ? 1 : use_default_flat_slope_roof_snow_load,
      flat_roof_snow_load: flat_roof_snow_load == null ? snow_load : flat_roof_snow_load,
    } : {}),
    ...(isRM5(product) || isRM10Evolution(product) ? {
      thermal_factor: thermal_factor || (isASCE722(building_code) ? 1.2 : 1),
      use_default_flat_slope_roof_snow_load: use_default_flat_slope_roof_snow_load == null ? 1 : use_default_flat_slope_roof_snow_load,
      flat_roof_snow_load: flat_roof_snow_load == null ? snow_load : flat_roof_snow_load,
    } : {}),
    ...(isRmGridflex5Rwdi(product) ? {
      allow_manual_attachments,
      additional_rail,
      blocks_orientation,
      is_ul3741_required,
      tilt: 5,
      use_friction,
      domestic_content,
      use_default_block_capacities : use_default_block_capacities,
      max_block_count : (max_block_count == 0) ? 0 : max_block_count || rmGridflex5dMaxBlockCount(isHalfBlockAllowed(is_half_block_allowed), modelData.height),
      min_block_count : (min_block_count == 0) ? 0 : min_block_count || minBlockCount(attachment_optimization_criteria === AttachmentOptimization.FULLY_ATTACHED, isHalfBlockAllowed(is_half_block_allowed), product_id),
      } : {}),
    ...(isRmGridflex10D(product) ? {
      use_default_block_capacities : use_default_block_capacities,
      max_block_count : (max_block_count == 0) ? 0 : max_block_count || rmGridflex10MaxBlockCount(isHalfBlockAllowed(is_half_block_allowed), projectConfiguration?.projectEnvConfig?.blocks_orientation),
      min_block_count : (min_block_count == 0) ? 0 : min_block_count || minBlockCount(attachment_optimization_criteria === AttachmentOptimization.FULLY_ATTACHED, isHalfBlockAllowed(is_half_block_allowed), product_id),
      use_default_max_block_north_corner : use_default_max_block_north_corner,
      max_block_north_corner_count: max_block_north_corner_count || rmGridflex10MaxBlockNorthCornerCount(isHalfBlockAllowed, modelData.height),
      allow_manual_attachments,
      additional_rail,
      blocks_orientation,
      is_ul3741_required,
      tilt: isRmGridflex10D(product)? tilt || 10: 5,
      use_friction}: {}),
    ...(isRM10Evolution(product) || isRM5(product) ? {
      central_support_choice: central_support_choice || (isRM5(product) && !greaterThanEqualToProjectVersion(projectConfiguration?.projectVersion, VERSION_MAP['rm5_mid_supports']) ? centralSupportChoices.SUPPLEMENTAL_BASE : centralSupportChoices.MID_SUPPORT),
    } : {}),
    seismic_sds,
    ...(isRmGridflex10D(product) ? {
      domestic_content }: {}),
    tornado_speed,
    seismic_sd1,
  };
}
